import React, {
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import gsap from 'gsap';
import useWindowSize from '@/hooks/useWindowSize';
import styles from 'components/blocks/header-rotating-shape/HeaderRotatingShape.module.scss';
import classNames from 'classnames';

const BACKGROUND_ICON_VIEWBOX_SIZE = 5000;
const BACKGROUND_ICON_CENTER = BACKGROUND_ICON_VIEWBOX_SIZE / 2;
const DEFAULT_ROTATION_DURATION = 180;

const isSafariBrowser = () => {
  if (typeof navigator === 'undefined') return false;

  return /^((?!chrome|android|crios|fxios|edgios).)*safari/i.test(navigator.userAgent);
};

type BackgroundSmallCurveProps = {
  iconColor?: string;
  isVisible?: boolean;
  initialRotation?: number;
  position?: Pick<React.CSSProperties, 'top' | 'right' | 'bottom' | 'left'>;
  revealDelay?: number;
  ref?: React.Ref<BackgroundSmallCurveRef>;
};

export type BackgroundSmallCurveRef = {
  elementRef: React.RefObject<SVGSVGElement | null>;
  revealTimelineRef: React.RefObject<gsap.core.Timeline>;
};

const defaultPosition: NonNullable<BackgroundSmallCurveProps['position']> = {
  top: '50%',
  left: '75%',
};

const BackgroundSmallCurve = ({
  iconColor = '#fff',
  isVisible,
  initialRotation = 0,
  position = defaultPosition,
  revealDelay = 1.5,
  ref,
}: BackgroundSmallCurveProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const backgroundIconMaskId = useId();
  const backgroundIconClipPathId = useId();
  const backgroundIconMaskFeatherId = useId();
  const bgIconRef = useRef<SVGGElement>(null);
  const bgIconMaskRef = useRef<SVGRectElement>(null);
  const bgIconClipPathRef = useRef<SVGRectElement>(null);
  const revealTimelineRef = useRef<gsap.core.Timeline>(gsap.timeline({ paused: true }));
  const bgRectRef = useRef<SVGRectElement>(null);
  const rotationTweenRef = useRef<gsap.core.Tween>(null);
  const rotationSpeedTweenRef = useRef<gsap.core.Tween>(null);
  const scaleTweenRef = useRef<gsap.core.Tween>(null);
  const [disableMaskFeather, setDisableMaskFeather] = useState(false);

  const { width: windowWidth, height: windowHeight } = useWindowSize();

  const rectangleWidth = 2 * windowWidth;
  const rectangleHeight = (100 * windowHeight) / 75;

  useEffect(() => {
    setDisableMaskFeather(isSafariBrowser());
  }, []);

  useLayoutEffect(() => {
    if (
      !bgIconMaskRef.current ||
      !bgIconClipPathRef.current ||
      !bgRectRef.current ||
      !bgIconRef.current
    )
      return undefined;

    const revealTimeline = revealTimelineRef.current;
    const maskShapes = [bgIconMaskRef.current, bgIconClipPathRef.current];

    revealTimeline.clear();
    revealTimeline
      .fromTo(
        [bgRectRef.current, bgIconRef.current],
        { opacity: 0 },
        {
          opacity: 1,
          duration: 0.1,
          ease: 'power2.in',
        },
        0,
      )
      .fromTo(
        maskShapes,
        {
          attr: {
            rx: '50%',
            ry: '50%',
          },
          '--mask-scale': 0,
        },
        {
          attr: {
            rx: 0,
            ry: 0,
          },
          '--mask-scale': 1,
          duration: 0.8,
          ease: 'power2.inOut',
        },
        revealDelay,
      )
      .pause(0);

    return () => {
      revealTimeline.clear();
    };
  }, [revealDelay, windowWidth, windowHeight]);

  useEffect(() => {
    if (!bgIconRef.current) return undefined;

    gsap.set(bgIconRef.current, { rotation: initialRotation, transformOrigin: '50% 50%' });
    rotationTweenRef.current = gsap.to(bgIconRef.current, {
      rotation: '+=360',
      duration: DEFAULT_ROTATION_DURATION,
      ease: 'none',
      repeat: -1,
      transformOrigin: '50% 50%',
    });
    scaleTweenRef.current = gsap.to(bgIconRef.current, {
      scaleY: 1.5,
      duration: DEFAULT_ROTATION_DURATION / 8,
      ease: 'power1.inOut',
      repeat: -1,
      yoyo: true,
      transformOrigin: '50% 50%',
    });

    return () => {
      scaleTweenRef.current?.kill();
      scaleTweenRef.current = null;
      rotationSpeedTweenRef.current?.kill();
      rotationSpeedTweenRef.current = null;
      rotationTweenRef.current?.kill();
      rotationTweenRef.current = null;
    };
  }, [initialRotation]);

  useEffect(() => {
    if (typeof isVisible !== 'boolean') return undefined;

    if (isVisible) {
      revealTimelineRef.current?.play();
    } else {
      revealTimelineRef.current?.reverse();
    }
    return undefined;
  }, [isVisible]);

  useImperativeHandle(
    ref,
    () => ({
      elementRef: svgRef,
      revealTimelineRef,
    }),
    [],
  );

  return (
    <svg
      width={BACKGROUND_ICON_VIEWBOX_SIZE}
      height={BACKGROUND_ICON_VIEWBOX_SIZE}
      viewBox={`0 0 ${BACKGROUND_ICON_VIEWBOX_SIZE} ${BACKGROUND_ICON_VIEWBOX_SIZE}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={classNames(styles.backgroundIcon, styles.smallCurve)}
      style={{
        ...position,
        // transform: 'scale(0.9)',
      }}
      ref={svgRef}
    >
      <defs>
        <clipPath id={backgroundIconClipPathId} clipPathUnits="userSpaceOnUse">
          <rect
            ref={bgIconClipPathRef}
            className={styles.backgroundIconMask}
            style={{ '--mask-scale': 0 } as React.CSSProperties}
            width={rectangleWidth}
            height={rectangleHeight}
            x={BACKGROUND_ICON_CENTER - rectangleWidth / 2}
            y={BACKGROUND_ICON_CENTER - rectangleHeight / 2}
          />
        </clipPath>
        <filter id={backgroundIconMaskFeatherId} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="90" />
        </filter>
        <mask
          id={backgroundIconMaskId}
          maskUnits="userSpaceOnUse"
          x={0}
          y={0}
          width={BACKGROUND_ICON_VIEWBOX_SIZE}
          height={BACKGROUND_ICON_VIEWBOX_SIZE}
        >
          <rect
            ref={bgIconMaskRef}
            className={styles.backgroundIconMask}
            style={{ '--mask-scale': 0 } as React.CSSProperties}
            width={rectangleWidth}
            height={rectangleHeight}
            x={BACKGROUND_ICON_CENTER - rectangleWidth / 2}
            y={BACKGROUND_ICON_CENTER - rectangleHeight / 2}
            fill="#fff"
            filter={`url(#${backgroundIconMaskFeatherId})`}
          />
        </mask>
      </defs>
      <g
        clipPath={disableMaskFeather ? `url(#${backgroundIconClipPathId})` : undefined}
        mask={disableMaskFeather ? undefined : `url(#${backgroundIconMaskId})`}
      >
        <g ref={bgIconRef} className={styles.backgroundIconSpin}>
          <rect
            width={BACKGROUND_ICON_VIEWBOX_SIZE}
            height={BACKGROUND_ICON_VIEWBOX_SIZE}
            fill={iconColor}
            ref={bgRectRef}
          />

          <path
            d="M2500.01 2500.01C3071.92 3071.92 3071.92 3999.17 2500.01 4571.08C1928.09 5142.99 1000.84 5142.99 428.932 4571.08"
            fill="var(--background-color)"
          />
          <path
            d="M2499.05 2499C2108.32 2108.27 2108.32 1474.78 2499.05 1084.05C2889.78 693.318 3523.28 693.318 3914.01 1084.05"
            fill="var(--background-color)"
          />
          <path
            d="M428.957 4571.06C-142.955 3999.15 -142.955 3071.9 428.957 2499.99C1000.87 1928.08 1928.12 1928.08 2500.03 2499.99"
            fill="var(--background-color)"
          />
          <path
            d="M2499.01 2498.95C2889.73 2889.68 3523.23 2889.68 3913.96 2498.95C4304.69 2108.22 4304.69 1474.73 3913.96 1084"
            fill="var(--background-color)"
          />
          <path
            d="M2500.01 2500.01C1928.1 3071.92 1000.85 3071.92 428.934 2500.01C-142.978 1928.1 -142.978 1000.85 428.934 428.941"
            fill="var(--background-color)"
          />
          <path
            d="M4571.06 4569.72C3999.14 5141.64 3071.89 5141.64 2499.98 4569.72C1928.07 3997.81 1928.07 3070.56 2499.98 2498.65"
            fill="var(--background-color)"
          />
        </g>
      </g>
    </svg>
  );
};

export default BackgroundSmallCurve;
